import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { dateFormattingOptions } from '@domain/formatting';
import { HaulierItem, LocaleValue } from '@hjc/masterdata-types';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocalizationService } from '@services/localization.service';
import { PartnersService } from '@services/partners.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, RowClickedEvent, themeAlpine } from 'ag-grid-community';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';

@Component({
  selector: 'md-pricing-logistics-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './pricing-logistics-page.component.html',
  styleUrl: './pricing-logistics-page.component.scss',
  standalone: true
})
export class PricingLogisticsPageComponent implements OnDestroy {
  // Inject services
  private partnersService = inject(PartnersService);
  private localizationService = inject(LocalizationService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  // State with signals
  isLoading = signal<boolean>(false);
  allPartners = signal<HaulierItem[]>([]);
  displayPartners = signal<HaulierItem[]>([]);
  lang = signal<LocaleValue>('fi');
  theme = themeAlpine;

  // Regular state
  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<HaulierItem>;

  // Computed column definitions
  columnDefs = computed(() => this.getColumnDefs());

  constructor() {
    // Initialize language from service
    this.lang.set(this.localizationService.currentLang);
    this.init();
  }

  private getColumnDefs(): ColDef<HaulierItem>[] {
    return [
      { headerName: this.translate.instant('FIELDS.IDENTIFIER'), field: 'id', sortable: true, flex: 1 },
      { headerName: this.translate.instant('INFO.NAME'), field: `name.${this.lang()}`, sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.STREET_ADDRESS'), field: 'address', sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.POSTAL_CODE'), field: 'zip', sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.CITY'), field: 'city', sortable: true, flex: 1 },
      { headerName: this.translate.instant('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: this.translate.instant('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: this.translate.instant('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: this.translate.instant('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ];
  }

  async init() {
    this.isLoading.set(true);

    try {
      const partners = (await this.partnersService.getLogisticsPartners())?.data || [];
      this.allPartners.set(partners);
      this.displayPartners.set(partners);

      this.initFuse();
      this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    } catch (error) {
      console.error('Failed to load logistics partners', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  initFuse() {
    this.fuse = new Fuse(this.allPartners(), {
      keys: ['companyCode', `name.${this.lang()}`, 'address', 'postalCode', 'city', 'phone', 'email'],
      includeScore: true,
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;
    this.displayPartners.set(term && term.length > 0 ? this.fuse.search(term).map(r => r.item) : this.allPartners());
  }

  onRowClicked(event: RowClickedEvent) {
    this.router.navigate([event.data.id], { relativeTo: this.activatedRoute });
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
